/**
 * TAHLEEL.ai — Agent Info Panel
 * Right panel showing agent status and real stats from latest analysis.
 */

import React, { useState, useEffect } from 'react';
import { AGENTS } from './AgentSidebar';
import { supabase } from '../services/api';

const AgentInfoPanel = ({ activeAgent }) => {
  const agent = AGENTS.find(a => a.id === activeAgent);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load latest analysis stats from Supabase
  useEffect(() => {
    const loadStats = async () => {
      setLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        const { data, error } = await supabase
          .from('analyses')
          .select('processing_time_seconds, confidence_score, analysis_data, vision_data, created_at, status')
          .order('created_at', { ascending: false })
          .limit(1);

        if (!error && data && data.length > 0) {
          const latest = data[0];
          const framesAnalyzed =
            latest.vision_data?.detections?.total_frames ||
            latest.analysis_data?.statistics?.frames_analyzed ||
            latest.analysis_data?.total_frames_analyzed ||
            0;
          const totalPlayers =
            latest.vision_data?.detections?.total_players ||
            latest.analysis_data?.statistics?.total_players ||
            0;
          const confidence = latest.confidence_score || 0;
          const processingTime = latest.processing_time_seconds || 0;
          const lastDate = latest.created_at
            ? new Date(latest.created_at).toLocaleDateString()
            : null;

          setStats({
            framesAnalyzed,
            totalPlayers,
            confidence: confidence > 0 ? `${confidence}%` : null,
            processingTime: processingTime > 0 ? `${processingTime}s` : null,
            lastAnalysis: lastDate,
            status: latest.status,
          });
        }
      } catch (err) {
        console.error('AgentInfoPanel: failed to load stats', err);
      }
      setLoading(false);
    };

    loadStats();
  }, []);

  const formatStat = (value) => {
    if (loading) return '...';
    if (value === null || value === undefined || value === 0) return '—';
    return String(value);
  };

  return (
    <div className="flex flex-col h-full bg-white border-l border-gray-200">

      {/* Header */}
      <div className="px-4 py-4 border-b border-gray-100">
        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Info</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">

        {/* Active Agent Info */}
        {agent && (
          <div>
            <p className="text-xs font-bold text-gray-800 mb-1">{agent.name}</p>
            <p className="text-[11px] text-gray-500">{agent.desc}</p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${agent.live ? 'bg-emerald-500' : 'bg-gray-300'}`}></span>
              <span className={`text-[10px] font-semibold ${agent.live ? 'text-emerald-600' : 'text-gray-400'}`}>
                {agent.live ? 'Running' : 'Coming Soon'}
              </span>
            </div>
          </div>
        )}

        {/* Stats from latest analysis */}
        <div className="space-y-3">
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Latest Analysis</p>
          {[
            { label: 'Frames analyzed', value: formatStat(stats?.framesAnalyzed) },
            { label: 'Confidence', value: formatStat(stats?.confidence) },
            { label: 'Processing time', value: formatStat(stats?.processingTime) },
            { label: 'Last analysis', value: formatStat(stats?.lastAnalysis) },
          ].map((stat, i) => (
            <div key={i} className="flex justify-between items-center">
              <span className="text-[11px] text-gray-500">{stat.label}</span>
              <span className="text-[11px] font-semibold text-gray-700">{stat.value}</span>
            </div>
          ))}

          {stats?.status && (
            <div className="flex justify-between items-center">
              <span className="text-[11px] text-gray-500">Status</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                stats.status === 'completed'
                  ? 'bg-emerald-50 text-emerald-600'
                  : stats.status === 'processing'
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                {stats.status}
              </span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100"></div>

        {/* All Agents Status */}
        <div>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">All Agents</p>
          <div className="space-y-1.5">
            {AGENTS.map(a => (
              <div key={a.id} className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${a.live ? 'bg-emerald-500' : 'bg-gray-300'}`}></span>
                <span className={`text-[11px] truncate ${
                  a.id === activeAgent ? 'font-semibold text-blue-600' : a.live ? 'text-gray-700' : 'text-gray-400'
                }`}>
                  {a.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentInfoPanel;
